// 화면 17 — 섹터 raw row 드로어 (PRD §5.7.2 P0).
// 우측 슬라이드 패널. 선택 섹터의 60일 raw row 표 + CSV 내보내기.
// 주의: smoothed_score 컬럼은 응답에 없음 — MVP: 응답 필드만 표시 (보고 (d) 참조).

import { memo, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { SectorHistoryRow } from './leadingSectors.types';

interface Props {
  open: boolean;
  sector: string | null;
  color: string;
  rows: SectorHistoryRow[]; // 해당 섹터 60일 row (ASC)
  onClose: () => void;
}

// CSV 열 — 응답에 있는 필드만. smoothed_score 는 별도 PR (보고 (d)).
const CSV_COLUMNS: (keyof SectorHistoryRow)[] = [
  'trading_date',
  'score_daily',
  'breadth',
  'peak',
  'avg_score',
  'stock_count',
  'is_smoothed_lead',
  'run_id',
];

const CSV_HEADERS: Record<string, string> = {
  trading_date: 'date',
  score_daily: 'score_daily',
  breadth: 'breadth',
  peak: 'peak',
  avg_score: 'avg_score',
  stock_count: 'stock_count',
  is_smoothed_lead: 'is_smoothed_lead',
  run_id: 'run_id',
};

export const SectorDetailDrawer = memo(function SectorDetailDrawer({
  open,
  sector,
  color,
  rows,
  onClose,
}: Props): JSX.Element | null {
  // ESC 키로 닫기.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const csvText = useMemo(() => buildCsv(rows), [rows]);

  if (!open || !sector) return null;

  const handleDownload = () => {
    // UTF-8 BOM (Excel 호환).
    const blob = new Blob(['﻿' + csvText], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sector_${sanitize(sector)}_${rows[0]?.trading_date ?? 'na'}_${rows[rows.length - 1]?.trading_date ?? 'na'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return createPortal(
    <div
      className="ls-drawer__backdrop"
      onClick={onClose}
      data-testid="ls-drawer-backdrop"
    >
      <aside
        className="ls-drawer"
        role="dialog"
        aria-label={`${sector} raw row 드로어`}
        data-testid="ls-drawer"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ls-drawer__header">
          <span className="ls-drawer__swatch" style={{ background: color }} aria-hidden />
          <h3 className="ls-drawer__title">{sector}</h3>
          <span className="ls-drawer__meta">{rows.length}거래일</span>
          <button
            type="button"
            className="ls-drawer__action"
            onClick={handleDownload}
            disabled={rows.length === 0}
            data-testid="ls-drawer-csv"
          >
            CSV 내보내기
          </button>
          <button
            type="button"
            className="ls-drawer__close"
            onClick={onClose}
            aria-label="드로어 닫기"
          >
            ×
          </button>
        </header>
        <div className="ls-drawer__body">
          {rows.length === 0 ? (
            <div className="ls-empty">표시할 row 가 없습니다.</div>
          ) : (
            <table className="ls-drawer__table">
              <thead>
                <tr>
                  {CSV_COLUMNS.map((c) => (
                    <th key={c}>{CSV_HEADERS[c]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.trading_date}-${r.run_id ?? ''}`}>
                    {CSV_COLUMNS.map((c) => (
                      <td key={c}>{fmtCell(r[c])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
});

function buildCsv(rows: SectorHistoryRow[]): string {
  const header = CSV_COLUMNS.map((c) => CSV_HEADERS[c]).join(',');
  const lines = rows.map((r) =>
    CSV_COLUMNS.map((c) => csvEscape(r[c])).join(',')
  );
  return [header, ...lines].join('\r\n') + '\r\n';
}

function csvEscape(v: unknown): string {
  if (v == null) return '';
  const s = typeof v === 'number' ? String(v) : String(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function fmtCell(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'number') {
    // 정수면 그대로, 실수면 소수 4자리 cap.
    return Number.isInteger(v) ? String(v) : v.toFixed(4);
  }
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  return String(v);
}

function sanitize(name: string): string {
  return name.replace(/[^\w가-힣]+/g, '_');
}
